<template>
    <div class="step-wrapper">
        <div class="step-section">
            <!-- Warning -->
            <infobox-generic
            v-if="paymentType === 'upload'"
            state="warning"
            :label="t('payments.batchfile.newPayment.step3.warningTitle')"
            :content="t('payments.batchfile.newPayment.step3.warningMessage')"/>
            <!-- Selected Account Section -->
            <divider-generic :applyBorder="false" icon="account-balance" :title="t('payments.batchfile.newPayment.step3.dividers.selectedAccount')"/>
            <text-block-generic :params="selectedAccountParams" :mask="ACCOUNT_NUMBER"/>
        </div>
        <!-- Batch File Information -->
        <div class="step-section">
            <divider-generic icon="info" :title="t('payments.batchfile.newPayment.step3.dividers.batchFileInfo')"/>
            <!-- File Name -->
            <text-block-generic :params="fileNameParams"/>
            <!-- Description -->
            <text-block-generic :params="descriptionParams"/>
            <!-- Operation Type -->
            <text-block-generic :params="operationTypeParams"/>
            <!-- Proccess Date -->
            <text-block-generic :params="processDateParams" :style="{'text-transform': 'capitalize'}"/>
            <!-- Reference -->
            <text-block-generic :params="referenceParams"/>
            <!-- Payments Divider -->
            <divider-generic icon="payment-single" :title="t('payments.batchfile.newPayment.step3.dividers.batchFileTable')"/>
        </div>
        <!-- Batch File Table -->
        <table-with-horizontal-scroll
        disabled
        noHoverStyles
        :standAlone="false"
        :headerData="tableHeader"
        :tableStyles="tableStyles"
        :tableData="parsedTableData"/>
        <div class="batchfile-entries-information">
            <span> {{numberOfEntries}} </span>
            <span> {{entriesTotalAmount}} </span>
        </div>
    </div>
</template>

<script setup>
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TableWithHorizontalScroll from '@/components/generic/table/WithHorizontalScroll'

/* Vue & Assets */
import { ref, computed } from 'vue'
import { ACCOUNT_NUMBER, ACCOUNTS_NIB_IBAN } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const {
    getBatchfileOperationTypes,
    getAccountsBalances
} = mapGetters(moduleMapper.PAYMENTS_BATCHFILE_NEW_PAYMENT)

/* Emmitters */
const emit = defineEmits(['scrollTo'])

const props = defineProps({
    paymentType: String,
    formData: Object,
    batchfileTypes: Array,
    batchfileEntries: Array
})

/*************************************************
*                                                *
*               Textblock Params                 *
*                                                *
*************************************************/

const selectedAccountParams = ref({
    label: t('payments.batchfile.newPayment.step3.account'),
    content: getAccountsBalances.value.find(account => account.id === props.formData.selectedAccountId).accountNumber
})

const fileNameParams = ref({
    label: t('payments.batchfile.newPayment.step3.fileName'),
    content: props.formData.fileName
})

const descriptionParams = ref({
    label: t('payments.batchfile.newPayment.step3.description'),
    content: props.formData.transferDescription
})

const operationTypeParams = ref({
    label: t('payments.batchfile.newPayment.step3.operationType'),
    content: getBatchfileOperationTypes.value.find(type => type.code === props.formData.operationType).description
})

const processDateParams = ref({
    label: t('payments.batchfile.newPayment.step3.processDate'),
    content: getDate(props.formData.processDate)
})

const referenceParams = ref({
    label: t('payments.batchfile.newPayment.step3.reference'),
    content: props.formData.reference
})

/*************************************************
*                                                *
*                  Table Data                    *
*                                                *
*************************************************/
const tableHeader = computed(() => {
    return [
        { label: t('payments.batchfile.newPayment.step3.tableHeaders.ibanLabel'), dataAttr: 'iban', rowCellType: 'title' },
        { label: t('payments.batchfile.newPayment.step3.tableHeaders.nameLabel'), dataAttr: 'name', rowCellType: 'description' },
        { label: t('payments.batchfile.newPayment.step3.tableHeaders.amountLabel'), dataAttr: 'amount', rowCellType: 'amount' },
        { label: t('payments.batchfile.newPayment.step3.tableHeaders.referenceLabel'), dataAttr: 'transferReference', rowCellType: 'description' },
        { label: t('payments.batchfile.newPayment.step3.tableHeaders.emailLabel'), dataAttr: 'email', rowCellType: 'description' }
    ]
})

const tableStyles = computed(() => {
    return [
        { width: '280px', 'min-width': '280px' },
        { width: '200px', 'min-width': '200px' },
        { width: '200px', 'min-width': '200px', 'text-align': 'left' },
        { width: '200px', 'min-width': '200px' },
        { width: '200px', 'min-width': '200px' }
    ]
})

const parsedTableData = computed(() => {
    return props.batchfileEntries.map((entry, index) => {
        const iban = entry.iban || entry.nba

        return [
            { value: index, dataAttr: 'id' },
            {
                type: 'title',
                value: { content: iban, mask: ACCOUNTS_NIB_IBAN },
                dataAttr: 'iban'
            },
            {
                type: 'description',
                value: entry.name,
                dataAttr: 'name'
            },
            {
                type: 'amount',
                value: { content: entry.amount, currency: props.formData.currency, noBalanceIcon: true },
                dataAttr: 'amount'
            },
            {
                type: 'description',
                value: entry.transferReference,
                dataAttr: 'transferReference'
            },
            {
                type: 'description',
                value: entry.email || '-',
                dataAttr: 'email'
            }
        ]
    })
})

const numberOfEntries = computed(() => {
    return t('payments.batchfile.newPayment.step3.entriesNumber', { entries: props.batchfileEntries.length })
})

const entriesTotalAmount = computed(() => {
    let totalAmount = 0

    props.batchfileEntries.forEach(entry => {
        totalAmount = totalAmount + entry.amount
    })

    return t('payments.batchfile.newPayment.step3.totalAmount', { amount: amountFormat(Math.abs(totalAmount), props.formData.currency) })
})
</script>

<style lang="scss" scoped>
.step-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    position: relative;
    gap: 24px;

    .step-section {
        width: 100%;
        max-width: 508px;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        gap: 24px;
    }

    :deep(.amount-content-wrapper) {
		justify-content: flex-start;
	}

    .batchfile-entries-information {
        @include H5-Medium;

        width: 100%;

        display: flex;
        align-items: flex-start;
        justify-content: space-between;

        color: var(--secondary-dark-2);
    }
}
</style>
